import { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  ArrowLeft, 
  Heart, 
  Share2, 
  ShoppingBag, 
  Star, 
  Plus, 
  Minus,
  Truck,
  Shield,
  RotateCcw,
  Leaf,
  CheckCircle,
  AlertTriangle,
  Eye,
  ChevronLeft,
  ChevronRight,
  Camera,
  Download,
  Copy,
  Facebook,
  Twitter,
  MessageCircle
} from "lucide-react";

// UI Components
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";

// Custom Components

import PageFooter from "@/components/PageFooter";
import SEOHelmet from "@/components/seo/SEOHelmet";
import { ProductImage } from "@/components/ui/GlobalImage";
import ProductReviews from "@/components/ProductReviews";
import { ProductRecommendations } from "@/components/ProductRecommendations";
import { WishlistButton } from "@/components/ui/WishlistButton";
import { RecentlyViewedProducts } from "@/components/RecentlyViewedProducts";
import { TranslationFallbackIndicator, FallbackDot } from "@/components/ui/TranslationFallbackIndicator";
// Services & Hooks
import { ProductService } from "@/services/productService";
import { useCart, useCurrency } from "@/stores";
import { useAuth } from "@/hooks/useAuth";
import { useWishlist } from "@/hooks/useWishlist";
import { useStock } from "@/hooks/useStock";
import { useShipping } from "@/hooks/useShipping";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useProductWithTranslation, useCurrentLocale, ProductWithTranslation } from "@/hooks/useTranslatedContent";

// Types & Interfaces
import { Product } from "@/shared/interfaces/Iproduct.interface";
import { StockInfo } from "@/services/stockService";

// Utils
import { sanitizeHtmlContent } from "@/utils/xssProtection";

interface ProductDetailProps {}

const ProductDetail: React.FC<ProductDetailProps> = () => {
  const { t } = useTranslation("pages");
  
  // Router & Navigation
  const { id } = useParams();
  const navigate = useNavigate();
  
  // Auth & Cart
  const { user } = useAuth();
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();
  
  // Product State
  const [product, setProduct] = useState<Product | ProductWithTranslation | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [relatedProducts, setRelatedProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Get current locale for translations
  const currentLocale = useCurrentLocale();
  
  // UI State
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [showImageZoom, setShowImageZoom] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState<string | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  
  // Refs
  const imageGalleryRef = useRef<HTMLDivElement>(null);
  const thumbnailsRef = useRef<HTMLDivElement>(null);
  
  // Hooks
  const { addToRecentlyViewed } = useRecentlyViewed();
  const { isInWishlist, toggleWishlist } = useWishlist();
  
  // Stock and shipping hooks
  const { stockInfo, canOrderQuantity } = useStock({ 
    productId: product?.id || 0, 
    enabled: !!product 
  });
  const singleStockInfo = stockInfo as StockInfo | null;
  
  const { getShippingMessage, isNantesMetropole } = useShipping({
    orderAmount: product ? product.price * quantity : 0,
    postalCode: '44000', // Default to Nantes for demo
    enabled: !!product
  });

  // Effects - refetch when locale changes
  useEffect(() => {
    const fetchProductData = async () => {
      if (!id) {
        setError(t("productDetail.error.missingId"));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Load all products for recommendations
        const products = await ProductService.getAllProducts();
        setAllProducts(products);

        // Find the product with the matching id
        const productId = parseInt(id);
        if (isNaN(productId)) {
          throw new Error(t("productDetail.error.invalidId"));
        }

        // Fetch product with translations
        const { getProductWithTranslation } = await import('@/services/translationService');
        const translatedProduct = await getProductWithTranslation(productId, currentLocale);

        if (!translatedProduct) {
          throw new Error(t("productDetail.error.notFound"));
        }

        setProduct(translatedProduct);
        
        // Add to recently viewed
        addToRecentlyViewed(translatedProduct as Product);

        // Get related products based on category and artisan
        const categoryProducts = products.filter(p => 
          p.id !== translatedProduct.id && 
          (p.category === translatedProduct.category || p.artisan === translatedProduct.artisan)
        );
        setRelatedProducts(categoryProducts.slice(0, 6));

        // Scroll to top
        window.scrollTo(0, 0);

      } catch (error) {
        console.error("Error fetching product:", error);
        setError(error instanceof Error ? error.message : t("productDetail.error.loadError"));
      } finally {
        setLoading(false);
      }
    };

    fetchProductData();
  }, [id, currentLocale, addToRecentlyViewed, t]);

  // Handlers
  const handleAddToCart = async () => {
    if (!product || quantity < 1) return;

    // Validate stock before adding to cart
    const canOrder = await canOrderQuantity(product.id, quantity);
    if (!canOrder.canOrder) {
      toast.error(canOrder.reason || t("productDetail.error.cartError"));
      return;
    }

    try {
      // Update global cart state directly
      addItem(product, quantity);

      // Show success message
      toast.success(`${quantity} × ${product.name} ${t("productDetail.cart.added")}`);
    } catch (error) {
      console.error("Error adding product to cart:", error);
      toast.error(t("productDetail.error.cartError"));
    }
  };

  const handleWishlistToggle = async () => {
    if (!product) return;
    await toggleWishlist(product.id);
  };

  const handleShare = async (platform: string) => {
    const url = window.location.href;
    const title = `${product?.name} - ${t("productDetail.share.brandName")}`;
    const text = t("productDetail.share.shareText", { name: product?.name });

    try {
      switch (platform) {
        case 'native':
          if (navigator.share) {
            await navigator.share({ title, text, url });
          } else {
            await navigator.clipboard.writeText(url);
            toast.success(t("productDetail.share.copied"));
          }
          break;
        case 'facebook':
          window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
          break;
        case 'twitter':
          window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
          break;
        case 'whatsapp':
          window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`, '_blank');
          break;
        case 'copy':
          await navigator.clipboard.writeText(url);
          toast.success(t("productDetail.share.copied"));
          break;
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error(t("productDetail.share.error"));
    }
    setShareMenuOpen(false);
  };

  const handleQuantityChange = (change: number) => {
    const newQuantity = quantity + change;
    const maxQuantity = singleStockInfo?.maxQuantity || 99;
    
    if (newQuantity >= 1 && newQuantity <= maxQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleImageChange = (direction: 'next' | 'prev') => {
    if (!product) return;
    
    const totalImages = product.images.length;
    if (direction === 'next') {
      setSelectedImage((prev) => (prev + 1) % totalImages);
    } else {
      setSelectedImage((prev) => (prev - 1 + totalImages) % totalImages);
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          {/* Breadcrumb Skeleton */}
          <div className="mb-8">
            <Skeleton className="h-4 w-64" />
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image Gallery Skeleton */}
            <div className="space-y-4">
              <Skeleton className="aspect-square w-full rounded-lg" />
              <div className="grid grid-cols-4 gap-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-md" />
                ))}
              </div>
            </div>
            
            {/* Product Info Skeleton */}
            <div className="space-y-6">
              <div>
                <Skeleton className="h-6 w-24 mb-4" />
                <Skeleton className="h-10 w-3/4 mb-4" />
                <Skeleton className="h-8 w-32 mb-6" />
              </div>
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  // Error State
  if (error || !product) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="max-w-md mx-auto">
            <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-serif text-foreground mb-4">
              {error || t("productDetail.error.notFound")}
            </h2>
            <p className="text-muted-foreground mb-8">
              {t("productDetail.error.description")}
            </p>
            <div className="space-x-4">
              <Button 
                variant="outline" 
                onClick={() => navigate(-1)}
                className="border-border"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t("productDetail.error.back")}
              </Button>
              <Button 
                asChild
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                <Link to="/products">
                  {t("productDetail.error.viewAll")}
                </Link>
              </Button>
            </div>
          </div>
        </div>
        <PageFooter />
      </div>
    );
  }

  const productRating = product.rating_average || 0;
  const reviewCount = product.rating_count || 0;

  return (
    <>
      <SEOHelmet 
        title={`${product.name} - ${product.category} ${t("productDetail.seo.handmadeBy")} ${product.artisan}`}
        description={product.short_description || product.description?.substring(0, 155)}
        image={product.images[0]}
        url={`/products/${product.id}`}
        type="product"
        product={{
          ...product,
          stock_quantity: singleStockInfo?.available ?? product.stock_quantity ?? 0,
          rating_average: productRating,
          rating_count: reviewCount
        }}
        keywords={[
          product.name,
          product.category,
          product.artisan,
          t("productDetail.seo.berberCraft"),
          t("productDetail.seo.handmade"),
          product.material || t("productDetail.seo.naturalFibers")
        ]}
      />

      <div className="min-h-screen bg-background">
        

        <main className="container mx-auto px-4 py-8">
          {/* Breadcrumbs */}
          <nav className="mb-8" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2 text-sm text-muted-foreground">
              <li>
                <Link to="/" className="hover:text-primary transition-colors">
                  {t("productDetail.breadcrumb.home")}
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/products" className="hover:text-primary transition-colors">
                  {t("productDetail.breadcrumb.shop")}
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to={`/products?category=${product.category}`} className="hover:text-primary transition-colors">
                  {product.category}
                </Link>
              </li>
              <li>/</li>
              <li className="text-foreground font-medium" aria-current="page">
                {product.name}
              </li>
            </ol>
          </nav>

          {/* Main Product Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
            {/* Image Gallery */}
            <section className="space-y-4" aria-label={t("productDetail.images.ariaLabel")}>
              {/* Main Image */}
              <div className="relative group">
                <div className="aspect-square overflow-hidden rounded-xl bg-muted">
                  <ProductImage
                    src={product.images[selectedImage]}
                    alt={`${product.name} - ${t("productDetail.images.view")} ${selectedImage + 1} ${t("productDetail.images.of")} ${product.images.length}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    preload={true}
                  />
                  
                  {/* Image Navigation */}
                  {product.images.length > 1 && (
                    <>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleImageChange('prev')}
                        aria-label={`${t("productDetail.images.previous")} (${selectedImage} ${t("productDetail.images.of")} ${product.images.length})`}
                      >
                        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleImageChange('next')}
                        aria-label={`${t("productDetail.images.next")} (${selectedImage + 2} ${t("productDetail.images.of")} ${product.images.length})`}
                      >
                        <ChevronRight className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </>
                  )}

                  {/* Zoom Button */}
                  <Dialog open={showImageZoom} onOpenChange={setShowImageZoom}>
                    <DialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="absolute top-4 right-4 bg-background/80 hover:bg-background shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl">
                      <DialogHeader className="sr-only">
                        <DialogTitle>{product.name} - {t("productDetail.images.enlarged")}</DialogTitle>
                        <DialogDescription>{t("productDetail.images.fullSizeDescription")}</DialogDescription>
                      </DialogHeader>
                      <ProductImage
                        src={product.images[selectedImage]}
                        alt={`${product.name} - ${t("productDetail.images.enlarged")}`}
                        className="w-full h-auto"
                      />
                    </DialogContent>
                  </Dialog>
                </div>

                {/* Image Indicators */}
                {product.images.length > 1 && (
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
                    {product.images.map((_, index) => (
                      <button
                        key={index}
                        className={`w-2 h-2 rounded-full transition-colors ${
                          index === selectedImage ? 'bg-primary-foreground' : 'bg-primary-foreground/50'
                        }`}
                        onClick={() => setSelectedImage(index)}
                        aria-label={`${t("productDetail.images.viewImage")} ${index + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {product.images.length > 1 && (
                <div className="grid grid-cols-4 gap-3" ref={thumbnailsRef}>
                  {product.images.map((image, index) => (
                    <button
                      key={index}
                      className={`aspect-square overflow-hidden rounded-lg border-2 transition-all ${
                        selectedImage === index
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-border hover:border-muted-foreground'
                      }`}
                      onClick={() => setSelectedImage(index)}
                    >
                      <ProductImage
                        src={image}
                        alt={`${product.name} - ${t("productDetail.images.thumbnail")} ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </section>

            {/* Product Information */}
            <section className="space-y-6" aria-label={t("productDetail.productInfo.ariaLabel")}>
              {/* Header */}
              <header>
                <div className="flex items-center gap-3 mb-3">
                  <Badge variant="outline" className="text-primary border-primary/20">
                    {product.category}
                  </Badge>
                  {product.is_new && (
                    <Badge className="bg-primary text-primary-foreground">
                      {t("productDetail.badges.new")}
                    </Badge>
                  )}
                  {product.is_featured && (
                    <Badge className="bg-accent text-accent-foreground">
                      {t("productDetail.badges.favorite")}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-3 flex-wrap mb-4">
                  <h1 
                    className="text-3xl lg:text-4xl font-serif text-foreground"
                    id="product-title"
                  >
                    {product.name}
                  </h1>
                  {/* Fallback indicator when translation is not available */}
                  {'_fallbackUsed' in product && (product as ProductWithTranslation)._fallbackUsed && (
                    <TranslationFallbackIndicator
                      isFallback={true}
                      displayedLocale={(product as ProductWithTranslation)._locale}
                      preferredLocale={currentLocale}
                      variant="badge"
                    />
                  )}
                </div>

                {/* Rating */}
                {reviewCount > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < Math.floor(productRating)
                              ? 'text-amber-400 fill-current'
                              : 'text-muted-foreground'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      ({reviewCount} {t("productDetail.reviews")})
                    </span>
                  </div>
                )}

                {/* Price */}
                <div className="mb-6">
                  <div className="text-3xl font-bold text-primary">
                    {formatPrice(product.price)}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {t("productDetail.price.vatIncluded")}
                  </p>
                </div>
              </header>

              {/* Short Description */}
              <div>
                <p className="text-muted-foreground leading-relaxed">
                  {product.short_description || product.description}
                </p>
                {product.description.length > 200 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-primary text-sm hover:underline mt-2"
                  >
                    {showFullDescription ? t("productDetail.description.showLess") : t("productDetail.description.showMore")}
                  </button>
                )}
              </div>

              {/* Quantity & Add to Cart */}
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center border rounded-lg">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      aria-label={t("productDetail.quantity.decrease")}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="px-4 py-2 min-w-12 text-center">
                      {quantity}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= (singleStockInfo?.maxQuantity || 99)}
                      aria-label={t("productDetail.quantity.increase")}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <WishlistButton
                    productId={product.id}
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleAddToCart}
                    className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground"
                    disabled={singleStockInfo?.isOutOfStock}
                  >
                    <ShoppingBag className="h-4 w-4 mr-2" />
                    {!singleStockInfo?.isOutOfStock ? t("productDetail.cart.add") : t("productDetail.cart.outOfStock")}
                  </Button>
                  
                  <Dialog open={shareMenuOpen} onOpenChange={setShareMenuOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="icon">
                        <Share2 className="h-4 w-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t("productDetail.share.title")}</DialogTitle>
                        <DialogDescription>{t("productDetail.share.description")}</DialogDescription>
                      </DialogHeader>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          onClick={() => handleShare('facebook')}
                          className="w-full"
                        >
                          <Facebook className="h-4 w-4 mr-2" />
                          Facebook
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('twitter')}
                          className="w-full"
                        >
                          <Twitter className="h-4 w-4 mr-2" />
                          Twitter
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('whatsapp')}
                          className="w-full"
                        >
                          <MessageCircle className="h-4 w-4 mr-2" />
                          WhatsApp
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => handleShare('copy')}
                          className="w-full"
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          {t("productDetail.share.copy")}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>

              {/* Stock & Shipping Info */}
              <div className="space-y-3">
                {singleStockInfo && (
                  <Alert>
                    <AlertDescription className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">
                        {!singleStockInfo.isOutOfStock 
                          ? `${t("productDetail.stock.inStock")} • ${singleStockInfo.available} ${t("productDetail.stock.available")}`
                          : t("productDetail.stock.outOfStock")
                        }
                      </span>
                    </AlertDescription>
                  </Alert>
                )}
                
                <Alert>
                  <AlertDescription className="flex items-center gap-2">
                    <Truck className="h-4 w-4 text-primary" />
                    <div className="text-sm">
                      <p className="font-medium text-foreground">{t("productDetail.shipping.free")}</p>
                      <p className="text-muted-foreground">
                        {t("productDetail.shipping.freeFrom")}
                      </p>
                        {getShippingMessage && (
                        <p className="text-muted-foreground mt-1">
                          {getShippingMessage()}
                        </p>
                      )}
                    </div>
                  </AlertDescription>
                </Alert>
              </div>
            </section>
          </div>

          {/* Product Details Tabs */}
          <div className="mb-16">
            <Tabs defaultValue="details" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-8">
                <TabsTrigger value="details">{t("productDetail.tabs.details")}</TabsTrigger>
                <TabsTrigger value="specs">{t("productDetail.tabs.specs")}</TabsTrigger>
                <TabsTrigger value="care">{t("productDetail.tabs.care")}</TabsTrigger>
                <TabsTrigger value="shipping">{t("productDetail.tabs.shipping")}</TabsTrigger>
              </TabsList>
              
              <TabsContent value="details" className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-medium text-foreground mb-4">{t("productDetail.detailsTab.title")}</h3>
                    <div
                      className="prose prose-stone dark:prose-invert max-w-none text-muted-foreground"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtmlContent(product.details),
                      }}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="specs" className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-medium text-foreground mb-4">{t("productDetail.specsTab.title")}</h3>
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {product.material && (
                        <>
                          <dt className="font-medium text-foreground">{t("productDetail.specsTab.material")}</dt>
                          <dd className="text-muted-foreground">{product.material}</dd>
                        </>
                      )}
                      {product.color && (
                        <>
                          <dt className="font-medium text-foreground">{t("productDetail.specsTab.color")}</dt>
                          <dd className="text-muted-foreground">{product.color}</dd>
                        </>
                      )}
                      {product.dimensions_cm && (
                        <>
                          <dt className="font-medium text-foreground">{t("productDetail.specsTab.dimensions")}</dt>
                          <dd className="text-muted-foreground">{product.dimensions_cm}</dd>
                        </>
                      )}
                      {product.weight_grams && (
                        <>
                          <dt className="font-medium text-foreground">{t("productDetail.specsTab.weight")}</dt>
                          <dd className="text-muted-foreground">{product.weight_grams}g</dd>
                        </>
                      )}
                      <dt className="font-medium text-foreground">{t("productDetail.specsTab.artisan")}</dt>
                      <dd className="text-muted-foreground">{product.artisan}</dd>
                      <dt className="font-medium text-foreground">{t("productDetail.specsTab.category")}</dt>
                      <dd className="text-muted-foreground">{product.category}</dd>
                    </dl>
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="care" className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-medium text-foreground mb-4">{t("productDetail.careTab.title")}</h3>
                    <div
                      className="prose prose-stone dark:prose-invert max-w-none text-muted-foreground"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtmlContent(product.care),
                      }}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
              
              <TabsContent value="shipping" className="space-y-4">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="font-medium text-foreground mb-4">{t("productDetail.shippingTab.title")}</h3>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-foreground mb-2">{t("productDetail.shippingTab.deliveryTime")}</h4>
                        <p className="text-muted-foreground">{t("productDetail.shippingTab.deliveryTimeValue")}</p>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground mb-2">{t("productDetail.shippingTab.shippingCost")}</h4>
                        <p className="text-muted-foreground">
                          {t("productDetail.shippingTab.shippingCostValue")}
                        </p>
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground mb-2">{t("productDetail.shippingTab.returns")}</h4>
                        <p className="text-muted-foreground">
                          {t("productDetail.shippingTab.returnsValue")}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Reviews Section */}
          <div className="mb-16">
            <ProductReviews 
              productId={product.id} 
              productName={product.name}
            />
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mb-16">
              <ProductRecommendations
                allProducts={relatedProducts}
                title={t("productDetail.recommendations")}
                maxRecommendations={6}
                onQuickView={() => {}}
              />
            </div>
          )}

          {/* Recently Viewed */}
          <div className="mb-16">
            <RecentlyViewedProducts />
          </div>
        </main>

        <PageFooter />
      </div>
    </>
  );
};

export default ProductDetail;